import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReportData } from "../../types/global.d";
import { UserData } from "../../api/auth";
import {
  ComplaintDraftSegment,
  ComplaintDraftValues,
  ComplaintFieldKey,
  createComplaintDraft,
  createComplaintMailtoUrl,
  getPersistableComplaintValues,
} from "../../utils/complaintDraft";
import "../styles/ComplaintDraftDialog.css";

const STORAGE_KEY = "complaintDraft.defaults.v1";
const HISTORY_KEY = "complaintDraft.history.v1";
const RESPONSE_EMAIL = "info@clean-nature.org";
const MAX_HISTORY_ITEMS = 8;

type ComplaintFieldHistory = Partial<Record<ComplaintFieldKey, string[]>>;

type FieldConfig = {
  key: ComplaintFieldKey;
  labelKey: string;
  type?: "text" | "email";
  multiline?: boolean;
};

const RECIPIENT_FIELDS: FieldConfig[] = [
  { key: "recipientTitle", labelKey: "complaintDraft.fields.recipientTitle" },
  { key: "recipientName", labelKey: "complaintDraft.fields.recipientName" },
  { key: "municipality", labelKey: "complaintDraft.fields.municipality" },
  {
    key: "recipientEmail",
    labelKey: "complaintDraft.fields.recipientEmail",
    type: "email",
  },
];

const SENDER_FIELDS: FieldConfig[] = [
  {
    key: "senderOrganization",
    labelKey: "complaintDraft.fields.senderOrganization",
  },
  { key: "senderPosition", labelKey: "complaintDraft.fields.senderPosition" },
  { key: "senderFullName", labelKey: "complaintDraft.fields.senderFullName" },
  {
    key: "senderContact",
    labelKey: "complaintDraft.fields.senderContact",
    type: "email",
  },
];

const REPORT_FIELDS: FieldConfig[] = [
  { key: "outgoingNumber", labelKey: "complaintDraft.fields.outgoingNumber" },
  {
    key: "locationDescription",
    labelKey: "complaintDraft.fields.locationDescription",
    multiline: true,
  },
  { key: "reportDate", labelKey: "complaintDraft.fields.reportDate" },
];

interface ComplaintDraftDialogProps {
  report: ReportData;
  user: UserData | null;
  onClose: () => void;
}

const copyText = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

const renderSegments = (segments: ComplaintDraftSegment[]) =>
  segments.map((segment, index) => (
    <span
      key={`${segment.placeholderKey ?? "text"}-${index}`}
      className={segment.placeholderKey ? "complaint-placeholder" : undefined}
    >
      {segment.text}
    </span>
  ));

const parseStoredJson = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch (error) {
    return fallback;
  }
};

const ComplaintDraftDialog: React.FC<ComplaintDraftDialogProps> = ({
  report,
  user,
  onClose,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<ComplaintDraftValues>({});
  const [fieldHistory, setFieldHistory] = useState<ComplaintFieldHistory>({});
  const [focusedField, setFocusedField] = useState<ComplaintFieldKey | null>(
    null
  );

  useEffect(() => {
    const saved = parseStoredJson<ComplaintDraftValues>(STORAGE_KEY, {});
    const history = parseStoredJson<ComplaintFieldHistory>(HISTORY_KEY, {});
    const userDefaults: ComplaintDraftValues = {
      senderFullName: user?.fullname || "",
      senderContact: RESPONSE_EMAIL,
    };

    setFieldHistory(history);
    setValues((current) => ({
      ...userDefaults,
      ...saved,
      senderContact: RESPONSE_EMAIL,
      ...current,
    }));
  }, [user?.fullname]);

  const draft = useMemo(
    () => createComplaintDraft(report, values),
    [report, values]
  );

  const updateField = (key: ComplaintFieldKey, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const persistDefaults = () => {
    const persistable = getPersistableComplaintValues(values);
    const nextHistory: ComplaintFieldHistory = { ...fieldHistory };

    Object.entries(persistable).forEach(([rawKey, rawValue]) => {
      const key = rawKey as ComplaintFieldKey;
      const value = rawValue?.trim();

      if (!value) {
        return;
      }

      const previous = nextHistory[key] ?? [];
      nextHistory[key] = [
        value,
        ...previous.filter((option) => option !== value),
      ].slice(0, MAX_HISTORY_ITEMS);
    });

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(persistable)
    );
    localStorage.setItem(HISTORY_KEY, JSON.stringify(nextHistory));
    setFieldHistory(nextHistory);
  };

  const handleCopy = async () => {
    try {
      persistDefaults();
      await copyText(`${draft.subject}\n\n${draft.body}`);
      window.alert(t("complaintDraft.copySuccess"));
    } catch (error) {
      window.alert(t("complaintDraft.copyError"));
    }
  };

  const handleOpenEmail = () => {
    try {
      persistDefaults();
      window.open(createComplaintMailtoUrl(draft), "_self");
    } catch (error) {
      window.alert(t("complaintDraft.emailError"));
    }
  };

  const renderSuggestions = (key: ComplaintFieldKey) => {
    const suggestions = fieldHistory[key] ?? [];

    if (focusedField !== key || suggestions.length === 0) {
      return null;
    }

    return (
      <div className="complaint-suggestions">
        {suggestions.map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            className="complaint-suggestion"
            data-testid={`complaint-suggestion-${key}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              updateField(key, suggestion);
              setFocusedField(null);
            }}
          >
            {suggestion}
          </button>
        ))}
      </div>
    );
  };

  const handleFieldBlur = (key: ComplaintFieldKey) => {
    window.setTimeout(() => {
      setFocusedField((current) => (current === key ? null : current));
    }, 120);
  };

  const renderField = ({ key, labelKey, type, multiline }: FieldConfig) => (
    <div key={key} className="complaint-field">
      <label htmlFor={`complaint-field-${key}`}>{t(labelKey)}</label>
      {multiline ? (
        <textarea
          id={`complaint-field-${key}`}
          data-testid={`complaint-field-${key}`}
          value={values[key] ?? ""}
          onChange={(event) => updateField(key, event.target.value)}
          onFocus={() => setFocusedField(key)}
          onBlur={() => handleFieldBlur(key)}
          placeholder={t("complaintDraft.fieldPlaceholder")}
          rows={3}
        />
      ) : (
        <input
          id={`complaint-field-${key}`}
          data-testid={`complaint-field-${key}`}
          type={type ?? "text"}
          value={values[key] ?? ""}
          onChange={(event) => updateField(key, event.target.value)}
          onFocus={() => setFocusedField(key)}
          onBlur={() => handleFieldBlur(key)}
          placeholder={t("complaintDraft.fieldPlaceholder")}
        />
      )}
      {renderSuggestions(key)}
    </div>
  );

  return (
    <>
      <div className="complaint-dialog-overlay" onClick={onClose} />
      <div
        className="complaint-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="complaint-dialog-title"
      >
        <div className="complaint-dialog-header">
          <div>
            <h2 id="complaint-dialog-title">{t("complaintDraft.title")}</h2>
            <p>{t("complaintDraft.subtitle")}</p>
          </div>
          <button
            className="complaint-dialog-close"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            ×
          </button>
        </div>

        <div className="complaint-dialog-content">
          <div className="complaint-form-grid">
            <section className="complaint-form-section">
              <h3>{t("complaintDraft.recipientSection")}</h3>
              {RECIPIENT_FIELDS.map(renderField)}
            </section>

            <section className="complaint-form-section">
              <h3>{t("complaintDraft.senderSection")}</h3>
              {SENDER_FIELDS.map(renderField)}
            </section>

            <section className="complaint-form-section complaint-form-section-wide">
              <h3>{t("complaintDraft.reportSection")}</h3>
              {REPORT_FIELDS.map(renderField)}
            </section>
          </div>

          <section className="complaint-preview">
            <h3>{t("complaintDraft.previewTitle")}</h3>
            <div className="complaint-preview-block">
              <div className="complaint-preview-label">
                {t("complaintDraft.subjectLabel")}
              </div>
              <div data-testid="complaint-subject-preview">
                {renderSegments(draft.subjectSegments)}
              </div>
            </div>

            <div className="complaint-preview-block">
              <div className="complaint-preview-label">
                {t("complaintDraft.bodyLabel")}
              </div>
              <pre data-testid="complaint-body-preview">
                {renderSegments(draft.bodySegments)}
              </pre>
            </div>
          </section>
        </div>

        <div className="complaint-dialog-actions">
          <button className="complaint-copy-btn" onClick={handleCopy}>
            <img
              src={`${process.env.PUBLIC_URL}/images/copy.png`}
              alt=""
              aria-hidden="true"
            />
            {t("complaintDraft.copyButton")}
          </button>
          <button className="complaint-email-btn" onClick={handleOpenEmail}>
            <img
              src={`${process.env.PUBLIC_URL}/images/send.png`}
              alt=""
              aria-hidden="true"
            />
            {t("complaintDraft.emailButton")}
          </button>
        </div>
      </div>
    </>
  );
};

export default ComplaintDraftDialog;
